/**
 * React Hook for Word Dictionary
 *
 * Provides a React hook interface for the WordDictionary service.
 * Handles Cloud Function validation with caching.
 */

import { useState, useEffect, useCallback } from 'react';
import wordDictionary from '../services/WordDictionary.js';

/**
 * Custom hook for using the word dictionary
 * @returns {Object} Dictionary state and methods
 */
export function useWordDictionary() {
  const [isLoaded, setIsLoaded] = useState(true); // Always ready with Cloud Functions
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Initialize (no-op for Cloud Function version, but loads cache)
    const initializeDictionary = async () => {
      try {
        await wordDictionary.initialize();
        setIsLoaded(true);
        setStats(wordDictionary.getStats());
      } catch (err) {
        console.error('Dictionary initialization error:', err);
        setError(err.message || 'Unknown error');
      }
    };

    initializeDictionary();
  }, []);

  /**
   * Validate a word using the dictionary (async with Cloud Function)
   * @param {string} word - Word to validate
   * @returns {Promise<boolean>} True if word is valid
   */
  const validateWord = useCallback(async (word) => {
    try {
      return await wordDictionary.validateWord(word);
    } catch (err) {
      console.error('Word validation error:', err);
      return false;
    }
  }, []);

  /**
   * Validate multiple words in batch
   * @param {Array<string>} words - Words to validate
   * @returns {Promise<Array>} Validation results
   */
  const validateWords = useCallback(async (words) => {
    try {
      return await wordDictionary.validateWords(words);
    } catch (err) {
      console.error('Batch validation error:', err);
      return [];
    }
  }, []);

  /**
   * Get current dictionary statistics
   * @returns {Object} Current stats
   */
  const getStats = useCallback(() => {
    return wordDictionary.getStats();
  }, []);

  /**
   * Get sample words for testing
   * @param {number} count - Number of words to return
   * @returns {Array<string>} Sample words
   */
  const getSampleWords = useCallback((count = 10) => {
    return wordDictionary.getSampleWords(count);
  }, []);

  /**
   * Clear the validation cache
   */
  const clearCache = useCallback(() => {
    wordDictionary.clearCache();
    setStats(wordDictionary.getStats());
  }, []);

  return {
    isLoaded,
    isLoading,
    error,
    stats,
    validateWord,
    validateWords,
    getStats,
    getSampleWords,
    clearCache,
    isReady: isLoaded && !error
  };
}

export default useWordDictionary;
